import {
  CHAT_DIALOG_SURFACE_CLASS_NAME,
  CHAT_DIALOG_HEADER_CLASS_NAME,
  CHAT_DIALOG_DESCRIPTION_CLASS_NAME,
} from "@/features/chat/chat-dialog-styles";
import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useMutation } from "@tanstack/react-query";
import { queryClient } from "@/ajax/queryClient";
import { StyledAsyncSelect } from "@/components/Selects/StyledAsyncSelect";
import AdminChat from "@/features/chat/admin/AdminChat";
import { useTRPC, useTRPCClient } from "@/trpc";

export const Route = createFileRoute("/_admin/admin/chat/")({
  component: AdminChatRoute,
});

function AdminChatRoute() {
  const trpc = useTRPC();
  const trpcClient = useTRPCClient();
  const [open, setOpen] = useState(false);
  const [selectedParticipants, setSelectedParticipants] = useState<
    Array<{ id: string; type: "user" | "organization"; label: string }>
  >([]);
  const [participantTypes, setParticipantTypes] = useState<
    Array<"user" | "organization" | null>
  >([null, null]);

  const createChatMutation = useMutation(
    trpc.chat.createChatThread.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.chat.adminListChatThreads.queryKey({}),
        });
        setOpen(false);
        setSelectedParticipants([]);
        setParticipantTypes([null, null]);
      },
    }),
  );

  const loadUsers = async (inputValue: string) => {
    const users = await trpcClient.adminDashboard.getUsers.query({
      search: inputValue,
    });

    return users.map((user) => ({
      value: user.id,
      label: `${user.first_name} ${user.last_name} (${user.email})`,
    }));
  };

  const loadBusinessUnits = async (inputValue: string) => {
    const businessUnits =
      await trpcClient.organization.searchBusinessUnits.query({
        search: inputValue,
      });

    return businessUnits.map((unit) => ({
      value: unit.id,
      label: `${unit.name} (${unit.organization.name})`,
    }));
  };

  const handleCreateChat = async () => {
    if (selectedParticipants.length !== 2) {
      return;
    }

    const userIds = selectedParticipants
      .filter((p) => p.type === "user")
      .map((p) => p.id);
    const organizationBusinessUnitIds = selectedParticipants
      .filter((p) => p.type === "organization")
      .map((p) => p.id);
    createChatMutation.mutate({
      userIds,
      organizationBusinessUnitIds,
    });
  };

  const handleCancel = () => {
    setOpen(false);
    setSelectedParticipants([]);
    setParticipantTypes([null, null]);
  };

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-50">
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className={`${CHAT_DIALOG_SURFACE_CLASS_NAME} gap-5 overflow-y-auto p-5 sm:p-6`}
        >
          <DialogHeader className={`${CHAT_DIALOG_HEADER_CLASS_NAME} pr-8`}>
            <DialogTitle>Create New Chat</DialogTitle>
            <DialogDescription className={CHAT_DIALOG_DESCRIPTION_CLASS_NAME}>
              Choose the participants for this conversation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            <div className="min-w-0 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
              <h3 className="mb-4 font-semibold text-slate-900">
                First Participant
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">
                    Type
                  </label>
                  <Select
                    value={participantTypes[0] || undefined}
                    onValueChange={(value: "user" | "organization") => {
                      setParticipantTypes([value, participantTypes[1]]);
                      setSelectedParticipants([]);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="organization">Organization</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {participantTypes[0] && (
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">
                      Select Participant
                    </label>
                    <StyledAsyncSelect
                      key={participantTypes[0]}
                      value={
                        selectedParticipants[0]?.id
                          ? {
                              value: selectedParticipants[0].id,
                              label: selectedParticipants[0].label,
                            }
                          : null
                      }
                      loadOptions={
                        participantTypes[0] === "user"
                          ? loadUsers
                          : loadBusinessUnits
                      }
                      onChange={(option) => {
                        if (!option) {
                          setSelectedParticipants(
                            selectedParticipants[1]
                              ? [selectedParticipants[1]]
                              : [],
                          );
                          return;
                        }
                        setSelectedParticipants(
                          selectedParticipants[1]
                            ? [
                                {
                                  id: option.value,
                                  type: participantTypes[0]!,
                                  label: option.label,
                                },
                                selectedParticipants[1],
                              ]
                            : [
                                {
                                  id: option.value,
                                  type: participantTypes[0]!,
                                  label: option.label,
                                },
                              ],
                        );
                      }}
                      defaultOptions
                      isClearable
                      className="min-w-0"
                      placeholder="Search participant..."
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="min-w-0 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
              <h3 className="mb-4 font-semibold text-slate-900">
                Second Participant
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">
                    Type
                  </label>
                  <Select
                    value={participantTypes[1] || undefined}
                    onValueChange={(value: "user" | "organization") => {
                      setParticipantTypes([participantTypes[0], value]);
                      setSelectedParticipants([selectedParticipants[0]]);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="organization">Organization</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {participantTypes[1] && (
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">
                      Select Participant
                    </label>
                    <StyledAsyncSelect
                      key={participantTypes[1]}
                      value={
                        selectedParticipants[1]?.id
                          ? {
                              value: selectedParticipants[1].id,
                              label: selectedParticipants[1].label,
                            }
                          : null
                      }
                      loadOptions={
                        participantTypes[1] === "user"
                          ? loadUsers
                          : loadBusinessUnits
                      }
                      onChange={(option) => {
                        if (!option) {
                          setSelectedParticipants(
                            selectedParticipants[0]
                              ? [selectedParticipants[0]]
                              : [],
                          );
                          return;
                        }
                        setSelectedParticipants(
                          selectedParticipants[0]
                            ? [
                                selectedParticipants[0],
                                {
                                  id: option.value,
                                  type: participantTypes[1]!,
                                  label: option.label,
                                },
                              ]
                            : [
                                {
                                  id: option.value,
                                  type: participantTypes[1]!,
                                  label: option.label,
                                },
                              ],
                        );
                      }}
                      defaultOptions
                      isClearable
                      className="min-w-0"
                      placeholder="Search participant..."
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              <Button
                variant="outline"
                className="flex-1"
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                disabled={
                  selectedParticipants.length !== 2 ||
                  createChatMutation.isPending
                }
                onClick={handleCreateChat}
              >
                {createChatMutation.isPending ? "Creating..." : "Create Chat"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div className="relative flex-1 min-h-0">
        <AdminChat onCreateChat={() => setOpen(true)} />
      </div>
    </div>
  );
}
